import {
  snackSupplierOptions,
  userMetadataOptions,
  useSimpleFoodCompany,
  useUser,
  userOptions,
} from "@/api";
import PageBackButton from "@/components/PageBackButton";
import {
  Breadcrumbs,
  Icon,
  LoadingDots,
  LoadingSpinner,
  PopupCard,
  ProgressIndicator,
} from "@app-components";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Food.module.scss";
import companySettingsStyles from "@/styles/CompanySettings.module.scss";
import { AnimatedTickingNumber } from "@/components/AnimatedTickingNumber";
import {
  calculateLunchPrice,
  NOK,
  NUMERIC_DATE_TIME_LOCALE,
} from "@/lib/utils";
import { UserAuthMetadata } from "@/types";
import UserTransactionsTable from "@/components/food/UserTransactionsTable";
import FoodDealModal from "@/components/food/FoodDealModal";
import DeactivateFoodUserButton from "@/components/food/DeactivateFoodUserButton";
import RefundFoodBalanceButton from "@/components/food/RefundFoodBalanceButton";
import {
  addMonths,
  differenceInDays,
  isAfter,
  startOfMonth,
  subMonths,
} from "date-fns";
import ActivateFoodUserForm from "@/components/food/ActivateFoodUserForm";
import { hasPendingRefund } from "@/lib/food";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import DisabledUserFoodPlaceholder from "@/components/food/DisabledUserFoodPlaceholder";
import { useRouterStore } from "@/store/zustandStore";
import { useFoodUserData } from "@/hooks/useFoodUserData";
import { useBuildingQuery } from "@/components/RouterSetup";

const inlineIconStyles = {
  display: "inline-block",
  marginLeft: "var(--wlcm-spacing-xs)",
  verticalAlign: "middle",
  textDecoration: "none",
};

export const Route = createFileRoute(
  "/_portal/lunch-admin/overview/users/$userId",
)({
  component: LoadingWrapper,
  loader: ({ params }) => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(userOptions(params.userId));
    queryClient.ensureQueryData(userMetadataOptions(params.userId));
    queryClient.ensureQueryData(snackSupplierOptions(supplierCompanyId));
  },
});

function LoadingWrapper() {
  useWelcomeFoodAccess();

  return (
    <Suspense
      fallback={
        <div className="center">
          <LoadingSpinner />
        </div>
      }
    >
      <RouteComponent />
    </Suspense>
  );
}

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userId } = Route.useParams();
  const { data: user } = useUser(userId);
  const { data: userMetadata } = useBuildingQuery(
    userMetadataOptions(userId),
  ) as {
    data?: UserAuthMetadata;
  };
  const { data: company, isLoading: isLoadingCompany } = useSimpleFoodCompany(
    user.buildingTenantId,
  );
  const {
    foodUser,
    usersDeals,
    userHasFreeDeal,
    isLoadingFoodUser,
    isLoadingDeals,
    hasLowBalance,
    hasCriticalBalance,
  } = useFoodUserData(user);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const lastLoginDate = userMetadata?.lastLogin
    ? new Date(userMetadata.lastLogin)
    : undefined;
  const isLastLoginRecent = lastLoginDate
    ? isAfter(lastLoginDate, subMonths(new Date(), 2))
    : false;

  const breadcrumbs = [
    { label: t("SIDEBAR.LUNCH_OVERVIEW"), path: "/lunch-admin/overview" },
    {
      label: t("SIDEBAR.INSPECT_LUNCH_USERS"),
      path: "/lunch-admin/overview/users",
    },
    {
      label: `${user.firstName} ${user.lastName}`,
      path: `/lunch-admin/overview/users/${userId}`,
    },
  ];

  const daysUntilNextMonth =
    differenceInDays(startOfMonth(addMonths(new Date(), 1)), new Date()) + 1;
  const balanceLowButNotCritical = hasLowBalance && !hasCriticalBalance;

  if ((isLoadingDeals || isLoadingFoodUser) && !userMetadata?.disabled) {
    return (
      <div className="center">
        <LoadingSpinner />
      </div>
    );
  }
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/lunch-admin/overview",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>
          {user.firstName} {user.lastName}{" "}
          <span style={{ opacity: 0.5, marginLeft: "var(--wlcm-spacing-sm)" }}>
            ({user.email})
          </span>
        </h1>
        <p
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-md)",
          }}
        >
          <span>
            <strong>{t("USERS.TABLE_HEADERS.PHONE")}: </strong>
            {user.phone ?? "-"}
          </span>
          <span>
            <strong>{t("USERS.TABLE_HEADERS.COMPANY")}: </strong>
            {isLoadingCompany ? (
              <LoadingDots />
            ) : company ? (
              <Link
                to="/lunch-admin/deals/$companyId"
                params={{ companyId: company.buildingTenantId }}
                style={{ color: "unset" }}
              >
                {company.buildingTenantName}
                <Icon name="arrow_forward" style={inlineIconStyles} />
              </Link>
            ) : (
              "-"
            )}
          </span>
          <span
            style={
              !isLastLoginRecent
                ? { color: "var(--wlcm-color-warning-orange)" }
                : {}
            }
          >
            {!isLastLoginRecent && (
              <Icon
                name="warning"
                style={{
                  ...inlineIconStyles,
                  marginLeft: 0,
                  marginRight: "var(--wlcm-spacing-xs)",
                }}
              />
            )}
            <strong>{t("USERS.TABLE_HEADERS.LAST_LOGIN")}: </strong>
            {lastLoginDate
              ? NUMERIC_DATE_TIME_LOCALE(i18n.language).format(lastLoginDate)
              : "-"}
          </span>
        </p>
      </div>
      {user.integratedWithWelcomeFood ? (
        <>
          {userMetadata?.disabled && (
            <div
              className="banner warningBanner"
              style={{
                margin: "var(--wlcm-spacing-sm) 0 var(--wlcm-spacing-md)",
                flexWrap: "wrap",
              }}
            >
              <Icon name="waving_hand" />
              <p>
                <Trans
                  i18nKey="USERS.USER_DISABLED"
                  components={{
                    hasDate: <span style={{ display: "none" }}></span>,
                  }}
                />
              </p>
            </div>
          )}
          <div className={styles.lunchCardsGrid}>
            {!!(company?.lunchCreditAmount || foodUser?.currentCreditBalance) &&
            !userHasFreeDeal ? (
              <div
                className={`${styles.lunchBalanceCard} ${styles.creditVariant} ${companySettingsStyles.numberCard} ${
                  hasCriticalBalance
                    ? `${companySettingsStyles.red} ${styles.red}`
                    : hasLowBalance
                      ? `${companySettingsStyles.orange} ${styles.orange}`
                      : ""
                }`}
              >
                <div
                  className={`${styles.creditContainer} ${!foodUser?.currentCreditBalance ? styles.noCreditLeft : ""}`}
                >
                  <p>{t("PAGES.LUNCH.CREDIT.LABEL")} </p>
                  <p>
                    <strong>
                      <AnimatedTickingNumber
                        to={foodUser?.currentCreditBalance}
                        formatter={NOK.format}
                      />
                    </strong>
                  </p>
                  <div style={{ marginTop: "var(--wlcm-spacing-xs)" }}>
                    <ProgressIndicator
                      id="credit-amount"
                      label={t("PAGES.LUNCH.CREDIT.REMAINING")}
                      hideLabel
                      value={foodUser?.currentCreditBalance || 0}
                      total={company?.lunchCreditAmount}
                      trackHeight={10}
                      trackColor={
                        !foodUser?.currentCreditBalance ||
                        balanceLowButNotCritical
                          ? "rgba(0,0,0,0.1)"
                          : "rgba(255,255,255,0.1)"
                      }
                      barColor={
                        balanceLowButNotCritical
                          ? "var(--wlcm-color-light-black)"
                          : "white"
                      }
                    />
                    <p
                      style={{
                        fontSize: "var(--wlcm-text-xs)",
                        opacity: 0.75,
                        marginTop: 3,
                      }}
                    >
                      {daysUntilNextMonth === 1
                        ? t("PAGES.LUNCH.CREDIT.RENEWAL_TOMORROW")
                        : t("PAGES.LUNCH.CREDIT.RENEWAL_IN_X_DAYS", {
                            count: daysUntilNextMonth,
                          })}
                    </p>
                  </div>
                </div>
                <div
                  className={`${styles.balanceContainer} ${foodUser?.currentCreditBalance ? styles.hasCredit : ""}`}
                >
                  <p>
                    {t("PAGES.LUNCH.BALANCE")}
                    <span className="sr-only">: </span>
                  </p>
                  <p>
                    <strong className="user-balance-number">
                      <AnimatedTickingNumber
                        to={foodUser?.currentBalance}
                        formatter={NOK.format}
                      />
                    </strong>
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={`${styles.lunchBalanceCard} ${companySettingsStyles.numberCard} ${userHasFreeDeal ? companySettingsStyles.gray : hasCriticalBalance ? companySettingsStyles.red : hasLowBalance ? companySettingsStyles.orange : ""}`}
              >
                {t("PAGES.LUNCH.BALANCE")}
                <span className="sr-only">: </span>
                <strong className="user-balance-number">
                  <AnimatedTickingNumber
                    to={foodUser?.currentBalance ?? 0}
                    formatter={NOK.format}
                  />
                </strong>
              </div>
            )}
            <div
              className={`${styles.lunchDealCard} ${companySettingsStyles.numberCard} ${companySettingsStyles.purple}`}
              {...(userMetadata?.disabled || !company
                ? {}
                : {
                    style: { cursor: "pointer" },
                    onClick: () => setDealModalOpen(true),
                  })}
            >
              <p>
                {usersDeals?.length === 1 && (
                  <>
                    {t("PAGES.LUNCH.PRICE_PER_LUNCH")}
                    <br />
                  </>
                )}
                <span style={{ opacity: 0.5 }}>
                  {t("PAGES.LUNCH.WITH_DEAL")}{" "}
                  <span style={{ fontWeight: "bold" }}>
                    {usersDeals?.[0]?.name}
                  </span>
                  {usersDeals?.length === 2 && (
                    <>
                      ,{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {usersDeals[1]?.name}
                      </span>
                    </>
                  )}
                </span>
                {!userMetadata?.disabled && !!company && (
                  <Icon
                    name="edit"
                    style={{
                      verticalAlign: "middle",
                      marginLeft: "var(--wlcm-spacing-xs)",
                      color: "var(--wlcm-color-malling-purple)",
                    }}
                  />
                )}
                <span className="sr-only">: </span>
              </p>
              <div
                className={styles.multiLunchDealCardInner}
                style={{
                  gridTemplateColumns:
                    usersDeals && usersDeals.length > 1
                      ? "1fr auto 1fr"
                      : "1fr",
                }}
              >
                <p>
                  <strong>
                    <AnimatedTickingNumber
                      to={calculateLunchPrice(
                        usersDeals?.[0]?.price ?? 0,
                        usersDeals?.[0]?.subsidyPercentage,
                      )}
                      formatter={NOK.format}
                    />
                  </strong>
                  {
                    /* Only display variant name if user has more than 1 deal */
                    usersDeals && usersDeals.length > 1 && (
                      <>
                        <br />
                        <span className={styles.lunchVariantName}>
                          {usersDeals[0].lunchVariant?.name}
                        </span>
                      </>
                    )
                  }
                </p>
                {usersDeals && usersDeals.length > 1 && (
                  <>
                    <p>
                      <strong style={{ opacity: 0.25 }}>/</strong>
                    </p>
                    <p>
                      <strong>
                        <AnimatedTickingNumber
                          to={calculateLunchPrice(
                            usersDeals[1]?.price ?? 0,
                            usersDeals[1]?.subsidyPercentage,
                          )}
                          formatter={NOK.format}
                        />
                      </strong>
                      <br />
                      <span className={styles.lunchVariantName}>
                        {usersDeals[1]?.lunchVariant?.name}
                      </span>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          <UserTransactionsTable
            userId={userId}
            buildingTenantId={user.buildingTenantId}
          />
          <DeactivateFoodUserButton
            userId={userId}
            buildingTenantId={user.buildingTenantId}
          />
          <PopupCard
            onClose={() => setDealModalOpen(false)}
            open={dealModalOpen && !userMetadata?.disabled && !!company}
          >
            <FoodDealModal
              users={[user]}
              company={company!}
              onClose={() => setDealModalOpen(false)}
              currentDealIds={foodUser?.dealIds}
            />
          </PopupCard>
        </>
      ) : (
        <div>
          {userMetadata?.disabled ? (
            <DisabledUserFoodPlaceholder />
          ) : isLoadingCompany ? (
            <LoadingSpinner />
          ) : company ? (
            <>
              <ActivateFoodUserForm
                user={user}
                company={company}
                hasPendingRefund={hasPendingRefund(foodUser?.pendingTransactions)}
              />
              <RefundFoodBalanceButton
                userId={userId}
                buildingTenantId={user.buildingTenantId}
                currentBalance={foodUser?.currentBalance ?? 0}
                pendingTransactions={foodUser?.pendingTransactions}
                deactivated={user.integratedWithWelcomeFood === false}
              />
            </>
          ) : (
            <p
              style={{
                fontWeight: "bold",
                color: "var(--wlcm-color-danger-red)",
              }}
            >
              {t("VALIDATION.SUBMIT_ERROR_DEFAULT_MESSAGE")}
            </p>
          )}
        </div>
      )}
    </>
  );
}
